/**
 * DocumentCard Component
 *
 * Displays a single document as a card with metadata and quick actions.
 */

import { FileText, FileImage, File, Download, Trash2, Eye, Sparkles } from 'lucide-react';
import { parseHighlightedSnippet } from '../../utils/documentSearch';

export interface DocumentSummary {
  documentId: string;
  caseFileId: string;
  fileName: string;
  fileType: string;
  fileSizeBytes: number;
  documentType: string;
  category?: string; // UI category label (e.g., "Medical Records", "IEP/504 Plans")
  summary?: string;
  tags?: string[];
  // Raw AI-extracted fields surfaced for client-side search (Phase A).
  extractedText?: string;
  extractedData?: Record<string, unknown>;
  // Reserved for Phase B (Vertex AI Search). Populated server-side when present.
  searchableText?: string;
  uploadedAt: { _seconds: number; _nanoseconds: number };
  uploadedBy: string;
  processingStatus: string;
  hasExtractedData: boolean;
}

interface DocumentCardProps {
  document: DocumentSummary;
  onClick?: () => void;
  onDownload?: (documentId: string) => void;
  onDelete?: (documentId: string) => void;
  /** Discovery Engine-provided match snippets (Phase B semantic search). */
  snippets?: string[];
  /**
   * Provenance of the match. `phaseB` results render with a Sparkles icon and
   * show snippets when present. `phaseA` is the default — keyword match only.
   */
  matchSource?: 'phaseA' | 'phaseB';
}

function getFileIcon(fileType: string): React.ReactNode {
  const type = fileType.toLowerCase();
  if (type.includes('pdf')) {
    return <FileText size={32} className="text-red-500" />;
  }
  if (type.includes('image') || type.includes('png') || type.includes('jpg') || type.includes('jpeg')) {
    return <FileImage size={32} className="text-blue-500" />;
  }
  if (type.includes('doc') || type.includes('word')) {
    return <FileText size={32} className="text-blue-600" />;
  }
  return <File size={32} className="text-muted" />;
}

function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
}

function formatTimestamp(timestamp: { _seconds: number }): string {
  if (!timestamp?._seconds) return '';
  const date = new Date(timestamp._seconds * 1000);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;

  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function getStatusBadge(status: string): { label: string; className: string } {
  switch (status.toLowerCase()) {
    case 'processed':
    case 'completed':
      return { label: 'Processed', className: 'badge badge-success' };
    case 'processing':
      return { label: 'Processing', className: 'badge badge-warning' };
    case 'pending':
    case 'pending_upload':
      return { label: 'Pending', className: 'badge badge-secondary' };
    case 'error':
    case 'failed':
      return { label: 'Failed', className: 'badge badge-danger' };
    default:
      return { label: status, className: 'badge' };
  }
}

export function DocumentCard({
  document,
  onClick,
  onDownload,
  onDelete,
  snippets,
  matchSource,
}: DocumentCardProps): JSX.Element {
  const statusBadge = getStatusBadge(document.processingStatus);
  const isSemantic = matchSource === 'phaseB';
  const showSnippets = isSemantic && snippets && snippets.length > 0;

  return (
    <div
      className={`card group hover:shadow-card-hover transition-all ${
        onClick ? 'cursor-pointer' : ''
      }`}
      onClick={onClick}
    >
      <div className="card-body">
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 w-12 h-12 bg-soft-neutral rounded-lg flex items-center justify-center">
            {getFileIcon(document.fileType)}
          </div>
          <div className="flex-1 min-w-0">
            <h4
              className="font-medium text-ink truncate flex items-center gap-1.5"
              title={document.fileName}
            >
              <span className="truncate">{document.fileName}</span>
              {isSemantic && (
                <Sparkles
                  size={14}
                  className="text-brass/70 flex-shrink-0"
                  aria-label="Semantic match"
                />
              )}
            </h4>
            <div className="flex items-center gap-2 mt-1">
              <span className="text-xs text-muted">
                {formatFileSize(document.fileSizeBytes)}
              </span>
              <span className="text-xs text-muted">•</span>
              <span className="text-xs text-muted">
                {formatTimestamp(document.uploadedAt)}
              </span>
            </div>
            <div className="mt-2">
              <span className={statusBadge.className}>{statusBadge.label}</span>
              {document.hasExtractedData && (
                <span className="badge badge-primary ml-1">AI Processed</span>
              )}
            </div>
          </div>
        </div>

        {/* Semantic-search snippets (Phase B). Discovery Engine returns
            <b>...</b> highlight markers; parse them into segments so React
            renders <mark> instead of escaping the tags as text. */}
        {showSnippets && (
          <div className="mt-3 space-y-1">
            {snippets!.slice(0, 2).map((snippet, idx) => (
              <p
                key={idx}
                className="text-xs text-muted leading-snug border-l-2 border-brass/30 pl-2"
              >
                {parseHighlightedSnippet(snippet).map((seg, sIdx) =>
                  seg.highlighted ? (
                    <mark
                      key={sIdx}
                      className="bg-brass/20 text-ink rounded px-0.5"
                    >
                      {seg.text}
                    </mark>
                  ) : (
                    <span key={sIdx}>{seg.text}</span>
                  )
                )}
              </p>
            ))}
          </div>
        )}

        {/* Quick actions - visible on hover */}
        <div className="mt-3 pt-3 border-t border-soft-neutral opacity-0 group-hover:opacity-100 transition-opacity flex gap-2">
          {onDownload && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onDownload(document.documentId);
              }}
              className="btn-ghost btn-sm flex items-center gap-1"
            >
              <Download size={14} />
              Download
            </button>
          )}
          <button
            onClick={(e) => {
              e.stopPropagation();
              onClick?.();
            }}
            className="btn-ghost btn-sm flex items-center gap-1"
          >
            <Eye size={14} />
            View
          </button>
          {onDelete && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onDelete(document.documentId);
              }}
              className="btn-ghost btn-sm flex items-center gap-1 text-red-600 hover:text-red-700"
            >
              <Trash2 size={14} />
              Delete
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default DocumentCard;
